import { Stack, Accordion, AccordionSummary, AccordionDetails, Typography, Chip } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import dayjs from "dayjs";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useDict } from "../../../../utils/useDict";
import { useFhirData } from "../../../../utils/useFhirData";
import { GenericTimeline } from "../GenericTimeline";
import { DiagnosticosAccordion } from "./DiagnosticosAccordion";
import { useState } from "react";

export function DiagnoticosHistory({
  reset,
  filter,
  setEditCondition,
}: {
  reset: any;
  filter: any;
  setEditCondition: (data: any) => void;
}) {
  const { summaryData, loadingDiagnostics } = useMedicalCareContext();

  const typeDiagnostic = {
    allergy: 1,
    condition: 0,
  };

  const condition = useFhirData.extractPatientSummaryResource(
    summaryData,
    "Condition"
  );
  const allergy = useFhirData.extractPatientSummaryResource(
    summaryData,
    "AllergyIntolerance"
  );

  const allergyData = allergy?.[0]?.entry?.map((el: any) => ({
    fullResource: el?.resource,
    type: "alergia",
    id: el?.resource?.id,
    criticality: useDict.criticality[el?.resource?.criticality] || "-",
    category: useDict.category[el?.resource?.category?.[0]] || "-",
    verificationStatus:
      useDict.verificationStatus[
        el?.resource?.verificationStatus?.coding?.[0]?.code
      ] || "",
    clinicalStatus:
      useDict.clinicalStatus[el?.resource?.clinicalStatus?.coding?.[0]?.code] ||
      "",
    description: el.resource?.code?.text,
    awnserDate: el?.resource?.recordedDate,
    displayInfo: {
      practitioner: useFhirData.findParticipant(
        el?.resource?.participant,
        "Practitioner"
      ).display,
      healthcare: null,
    },
  }));

  const conditionData = condition?.[0]?.entry?.map((el: any) => {
    return {
      fullResource: el?.resource,
      id: el?.resource?.id,
      cid10: el?.resource?.code?.coding?.[0]?.code,
      description: el?.resource?.code?.coding?.[0]?.display || "",
      awnserDate: el?.resource?.recordedDate,
      displayInfo: {
        practitioner: useFhirData.findParticipant(
          el?.resource?.participant,
          "Practitioner"
        ).display,
        healthcare: null,
      },
      verificationStatus:
        useDict.verificationStatus[
          el?.resource?.verificationStatus?.coding?.[0]?.code
        ] || "",
      clinicalStatus:
        useDict.clinicalStatus[
          el?.resource?.clinicalStatus?.coding?.[0]?.code
        ] || "",
      practitioner: useFhirData.findParticipant(
        el?.resource?.participant,
        "Practitioner"
      )?.display,
      informant: useFhirData.findParticipant(
        el?.resource?.participant,
        "Patient"
      )
        ? "Paciente"
        : "Profissional",
    };
  });

  let filterData =
    filter === typeDiagnostic?.condition ? conditionData : allergyData;
  if (loadingDiagnostics) {
    filterData?.unshift({ loadingData: loadingDiagnostics });
  }

  // Agrupa diagnósticos/alergias por dia
  const groupedByDay = filterData?.reduce((acc: any, item: any) => {
    // Ignora item de loading
    if (item.loadingData) return acc;
    
    const day = dayjs(item.awnserDate).format("DD/MM/YYYY");
    if (!acc[day]) {
      acc[day] = {
        day,
        awnserDate: item.awnserDate,
        items: [],
      };
    }
    acc[day].items.push(item);
    return acc;
  }, {}) || {};

  // Converte para array para usar na timeline
  const timelineData = Object.values(groupedByDay);

  return (
    <Stack spacing={1}>
      <GenericTimeline
        data={timelineData}
        component={(data: any, index: number) => (
          <DayAccordion 
            day={data.day} 
            items={data.items} 
            reset={reset}
            filter={filter}
            setEditCondition={setEditCondition}
            defaultExpanded={index === 0} 
          />
        )}
      />
    </Stack>
  );
}

// Componente Accordion que agrupa diagnósticos por dia
function DayAccordion({ day, items, reset, filter, setEditCondition, defaultExpanded }: any) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        width: "100%",
        boxShadow: "1px 1px 16px 1px rgba(0, 0, 0, 0.1)",
        border: "1px solid",
        borderColor: "neutral200.main",
        borderRadius: 1,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={2} width="100%">
          <Chip 
            label={`${items.length} ${items.length === 1 ? 'registro' : 'registros'}`}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Stack>
      </AccordionSummary>
      
      <AccordionDetails>
        <Stack spacing={1}>
          {items.map((data: any, idx: number) => (
            <DiagnosticosAccordion
              key={idx}
              reset={reset}
              data={data}
              page={filter}
              index={idx}
              setEditCondition={setEditCondition}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
